import React, { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";

export default function AuthorizedPersons() {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    id: null,
    name: "",
    mobile: "",
    role_title: "",
    is_active: true,
  });

  const isEditing = !!form.id;

  const loadPersons = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/fpo-authorized-persons`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load authorised persons");
      }

      setPersons(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Load authorised persons error:", err);
      setMessage(err.message || "Failed to load authorised persons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPersons();
  }, []);

  const resetForm = () => {
    setForm({
      id: null,
      name: "",
      mobile: "",
      role_title: "",
      is_active: true,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEdit = (person) => {
    setForm({
      id: person.id,
      name: person.name || "",
      mobile: person.mobile || "",
      role_title: person.role_title || "",
      is_active: person.is_active !== false,
    });
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!form.name.trim()) {
      setMessage("Name is required");
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        mobile: form.mobile.trim() || null,
        role_title: form.role_title.trim() || null,
        is_active: form.is_active,
      };

      const url = isEditing
        ? `${API_BASE}/fpo-authorized-persons/${form.id}`
        : `${API_BASE}/fpo-authorized-persons`;

      const res = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save authorised person");
      }

      setMessage(
        isEditing
          ? "Authorised person updated successfully"
          : "Authorised person created successfully"
      );

      resetForm();
      await loadPersons();
    } catch (err) {
      console.error("Save authorised person error:", err);
      setMessage(err.message || "Failed to save authorised person");
    }
  };

  return (
    <div>
      <h2>FPO Authorised Persons</h2>

      <p style={{ color: "#555" }}>
        Maintain the FPO persons who are authorised to receive cash from agents.
        Only active persons will appear in the mobile app during cash payment
        request.
      </p>

      {message && (
        <div
          style={{
            marginBottom: 16,
            padding: 10,
            background: "#f5f5f5",
            border: "1px solid #ddd",
          }}
        >
          {message}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(160px, 1fr)) auto auto",
          gap: 10,
          alignItems: "end",
          marginBottom: 24,
        }}
      >
        <div>
          <label>Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Person name"
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <div>
          <label>Mobile</label>
          <input
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            placeholder="Mobile number"
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <div>
          <label>Role / Designation</label>
          <input
            name="role_title"
            value={form.role_title}
            onChange={handleChange}
            placeholder="Treasurer / Accountant / Staff"
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            paddingBottom: 8,
          }}
        >
          <input
            type="checkbox"
            name="is_active"
            checked={form.is_active}
            onChange={handleChange}
          />
          Active
        </label>

        <button
          type="submit"
          style={{
            padding: "9px 14px",
            background: "#1976D2",
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
        >
          {isEditing ? "Update" : "Create"}
        </button>

        {isEditing && (
          <button
            type="button"
            onClick={resetForm}
            style={{
              padding: "9px 14px",
              background: "#777",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        )}
      </form>

      <h3>Authorised Person List</h3>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table
          border="1"
          cellPadding="8"
          cellSpacing="0"
          style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th>ID</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Role / Designation</th>
              <th>Status</th>
              <th>Updated At</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {persons.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No authorised person found
                </td>
              </tr>
            ) : (
              persons.map((person) => (
                <tr key={person.id}>
                  <td>{person.id}</td>
                  <td>{person.name}</td>
                  <td>{person.mobile || "-"}</td>
                  <td>{person.role_title || "-"}</td>
                  <td>{person.is_active ? "Active" : "Inactive"}</td>
                  <td>
                    {person.updated_at
                      ? new Date(person.updated_at).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleEdit(person)}
                      style={{
                        padding: "6px 10px",
                        background: "#f97316",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}